import { I18nProvider, i18n } from '@hbs-us/ui';
import { QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { AuthGuard } from './auth/AuthGuard.js';
import { JoinRoute } from './features/join/JoinRoute.js';
import {
  type HandoffMode,
  SessionHandoffRoute,
} from './features/session-handoff/SessionHandoffRoute.js';
import { setupI18n } from './i18n.js';
import { initializeAppearance } from './lib/appearance.js';
import { queryClient } from './lib/queryClient.js';
import { AppRouter } from './router.js';
import './styles.css';
// Side-effect import: wires refreshSession into apiFetch's 401 single-flight.
import './auth/authClient.js';

setupI18n();
initializeAppearance();

function readJoinCode(pathname: string): string | null {
  const normalized =
    pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname;
  const prefix = '/join/';
  if (!normalized.startsWith(prefix)) return null;
  const code = normalized.slice(prefix.length);
  if (code.length === 0 || code.includes('/')) return null;
  return decodeURIComponent(code);
}

/**
 * Boot-time session-handoff paths (Plan 22-29), matched BEFORE the AuthGuard tree exactly like the
 * /join intercept: `/impersonation/start` (admin handoff redemption) and `/demo-access` (self-serve
 * demo). Trailing slashes tolerated; the query string (the impersonation `?token=`) is ignored here
 * and read inside the intercept.
 */
function readHandoffMode(pathname: string): HandoffMode | null {
  const normalized =
    pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname;
  if (normalized === '/impersonation/start') return 'impersonation';
  if (normalized === '/demo-access') return 'demo';
  return null;
}

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found');
}

const joinCode = typeof window !== 'undefined' ? readJoinCode(window.location.pathname) : null;
const handoffMode =
  typeof window !== 'undefined' ? readHandoffMode(window.location.pathname) : null;

function BootTree() {
  if (joinCode) {
    return <JoinRoute code={joinCode} />;
  }
  if (handoffMode) {
    return (
      <SessionHandoffRoute mode={handoffMode}>
        <AuthGuard>
          <AppRouter />
        </AuthGuard>
      </SessionHandoffRoute>
    );
  }
  return (
    <AuthGuard>
      <AppRouter />
    </AuthGuard>
  );
}

createRoot(rootEl).render(
  <StrictMode>
    <I18nProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <BootTree />
      </QueryClientProvider>
    </I18nProvider>
  </StrictMode>,
);
